import { XHR_READY, XHR_SUCCESS_CODE } from '@opengov/stories-shared/constants';
import type { IProps } from '@tinymce/tinymce-react/lib/cjs/main/ts/components/Editor';

import { getS3SignUrl } from '../../../../../../common/components/ImageUploader/ImageUploader';
import { purgeUrlCache } from '../../../../../../utils';
import { ISubtypeColors } from '../../../../../../utils/editor';

export const commonToolbar = [
  'undo redo',
  'bold italic underline strikethrough',
  'link image',
  'blocks fontfamily fontsizeinput',
  'alignleft aligncenter alignright alignjustify lineheight',
  'editimage blockquote forecolor backcolor removeformat',
  'subscript superscript charmap'
];

export interface ITinyMCEDialog {
  block(msg: string): void;
  unblock(): void;
}

interface IFetchSignedUrlPayload {
  file: File;
}
interface IFetchSignedUrlResponse {
  signedRequest: string;
  url: string;
}
// fetch (not XHR): in PSP mode auth (bearer + credentials) is attached by
// the window.fetch wrapper in psp/pspEntry.tsx — raw XHR bypasses it and
// the sign request 401s. The S3 PUT below stays on XHR; it must not carry
// our auth headers anyway.
const fetchSignedUrl = async (
  entitySubdomain: string,
  { file }: IFetchSignedUrlPayload
): Promise<IFetchSignedUrlResponse> => {
  const url = getS3SignUrl({ fileName: file.name, fileType: file.type, entitySubdomain });
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Image url creation failed. Code: ${res.status}`);
  }
  return res.json();
};

interface IUploadFilePayload {
  file: File;
}
const uploadFile = async (url: string, { file }: IUploadFilePayload): Promise<string> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.onreadystatechange = () => {
      if (xhr.readyState === XHR_READY) {
        if (xhr.status === XHR_SUCCESS_CODE) {
          resolve(xhr.responseText);
        } else {
          reject(new Error(`Image upload failed. Code: ${xhr.status}`));
        }
      }
    };
    xhr.send(file);
  });
};

export const getFilePickerCallback =
  (
    entitySubdomain: string,
    activeDialogRef: React.MutableRefObject<ITinyMCEDialog>
  ): IProps['init']['file_picker_callback'] =>
  (callback) => {
    const input = document.createElement('input');

    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.addEventListener('change', async (event: Event) => {
      const file = (event.target as HTMLInputElement).files[0];

      try {
        activeDialogRef?.current?.block('Loading...');
        const { signedRequest, url } = await fetchSignedUrl(entitySubdomain, { file });
        await uploadFile(signedRequest, { file });
        callback(url, { title: file.name });
      } catch (error) {
        console.error(error);
      } finally {
        activeDialogRef?.current?.unblock();
      }
    });
    input.click();
  };

export const getImagesUploadHandler =
  (entitySubdomain: string): IProps['init']['images_upload_handler'] =>
  async (blobInfo) =>
    new Promise(async (resolve, reject) => {
      const blob = blobInfo.blob();
      const file = new File([blob], blobInfo.filename(), { type: blob.type });

      try {
        const { signedRequest, url } = await fetchSignedUrl(entitySubdomain, { file });
        await uploadFile(signedRequest, { file });
        resolve(purgeUrlCache(url));
      } catch (error) {
        reject(error);
      }
    });

export const getContentOptions = (isSectionContent: boolean): Partial<IProps['init']> => {
  const toolbar = [...commonToolbar];
  if (isSectionContent) {
    toolbar[2] = [toolbar[2], 'table'].join(' ');
    toolbar[4] = [toolbar[4], 'numlist', 'bullist'].join(' ');
  }
  return {
    toolbar: toolbar.join(' | ')
  };
};

export const getForegroundColorOptions = ({
  swatch,
  customSwatch,
  active
}: ISubtypeColors): Partial<IProps['init']> => ({
  color_map_foreground: [...swatch, ...customSwatch].reduce(
    // TinyMCE requires color hex and name that is why we have "color, color" here
    (swatch, color) => [...swatch, color, color],
    []
  ),
  color_default_foreground: active
});
